import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useEffect, useState } from "react";
import { Loader } from "../../components/CustomLoader";
import { teamManagementApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { formatPrice } from "../../utils/formatNumber";
import { fDateOnly } from "../../utils/formatTime";
import Iconify from "../../components/iconify";
import StatusBTN from "../../components/StatusBTN";

const ViewTeamManagement = ({ id, show, onHide, leagueData, teamID }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const positionNames = {
    24: "Goalkeeper",
    25: "Defender",
    26: "Midfielder",
    27: "Forward"
  };

  const fetchPlayerDetails = async () => {
    try {
      setLoading(true);
      const response = await teamManagementApi.getById({ id });
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchPlayerDetails();
  }, [id]);

  const InfoField = ({ label, value, icon, color = "primary" }) => (
    <Grid item xs={12} sm={6} md={4}>
      <Card className="view-team-info-card">
        <CardContent className="view-team-info-card-content">
          <Box>
            <Iconify
              icon={icon}
              className={`view-team-info-icon ${color}`}
            />
          </Box>
          <Typography className="view-team-info-label">
            {label}
          </Typography>
          <Typography className="view-team-info-value">
            {value || "-"}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const PlayerHeader = () => (
    <Box className="view-team-player-header">
      <Box className="view-team-avatar-container">
        <Avatar
          src={data?.imagePath}
          alt={data?.fullName || "Player"}
          className="view-team-avatar"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://cdn.sportmonks.com/images/soccer/placeholder.png";
          }}
        />
        <Box
          className={`view-team-status-dot ${data?.status === 1 ? 'active' : 'inactive'}`}
        />
      </Box>

      <Typography variant="h4" className="view-team-player-name">
        {data?.fullName || "Player Name"}
      </Typography>

      <Typography variant="h6" className="view-team-display-name">
        {data?.displayName || "Display Name"}
      </Typography>

      <Stack className="view-team-chips-container">
        <Chip
          label={data?.leagueTeams?.find(team => team.teamId === String(teamID))?.teamName || "Team"}
          color="primary"
          variant="outlined"
          className="view-team-team-chip"
        />
        <StatusBTN
          status={data?.status === 1}
          className="view-team-status-btn"
        />
      </Stack>
    </Box>
  );

  const LeagueTeamsSection = () => {
    const currentTeam = data?.leagueTeams?.find(team => team.teamId === String(teamID));

    return (
      <Box className="view-team-league-teams-section">
        <Typography variant="h6" className="view-team-section-title">
          Team Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card className="view-team-info-card">
              <CardContent className="view-team-info-card-content">
                <Box className="view-team-team-logo-container">
                  {currentTeam?.teamImage ? (
                    <img
                      src={currentTeam.teamImage}
                      alt={currentTeam.teamName || "Team"}
                      className="view-team-team-logo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://cdn.sportmonks.com/images/soccer/placeholder.png";
                      }}
                    />
                  ) : (
                    <Iconify
                      icon="eva:people-outline"
                      className="view-team-info-icon primary"
                    />
                  )}
                </Box>
                <Typography className="view-team-info-label">
                  Current Team
                </Typography>
                <Typography className="view-team-info-value">
                  {currentTeam?.teamName || "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={show}
      onClose={onHide}
      PaperProps={{
        className: "view-team-dialog"
      }}
    >
      <DialogTitle className="view-team-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" className="view-team-title">
            Player Profile
          </Typography>
          <Tooltip title="Close">
            <IconButton
              onClick={onHide}
              className="view-team-close-btn"
            >
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {loading ? (
        <Box className="view-team-loading-container">
          <Loader />
        </Box>
      ) : (
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <PlayerHeader />

            <Divider className="view-team-divider" />

            {/* Basic Information */}
            <Typography variant="h6" className="view-team-section-title">
              Basic Information
            </Typography>
            <Grid container spacing={3} className="view-team-section-grid">
              <InfoField
                label="First Name"
                value={data?.firstName || "-"}
                icon="eva:person-outline"
                color="info"
              />

              <InfoField
                label="Last Name"
                value={data?.lastName || "-"}
                icon="eva:person-outline"
                color="info"
              />

              <InfoField
                label="Common Name"
                value={data?.commonName || "-"}
                icon="eva:person-outline"
                color="primary"
              />

              <InfoField
                label="Display Name"
                value={data?.displayName || "-"}
                icon="eva:person-outline"
                color="secondary"
              />

              <InfoField
                label="Date of Birth"
                value={data?.dateOfBirth ? fDateOnly(data.dateOfBirth) : "-"}
                icon="eva:calendar-outline"
                color="info"
              />

              <InfoField
                label="Gender"
                value={capitalize(data?.gender || "-")}
                icon="eva:person-outline"
                color="warning"
              />
            </Grid>

            {/* Physical Information */}
            <Typography variant="h6" className="view-team-section-title">
              Physical Information
            </Typography>
            <Grid container spacing={3} className="view-team-section-grid">
              <InfoField
                label="Height"
                value={data?.height ? `${data.height} cm` : "-"}
                icon="eva:trending-up-outline"
                color="success"
              />

              <InfoField
                label="Weight"
                value={data?.weight ? `${data.weight} kg` : "-"}
                icon="eva:scale-outline"
                color="success"
              />

            </Grid>

            {/* Game Information */}
            <Typography variant="h6" className="view-team-section-title">
              Game Information
            </Typography>
            <Grid container spacing={3} className="view-team-section-grid">
              <InfoField
                label="Game Points"
                value={data?.gamePoints ? data.gamePoints.toFixed(2) : "-"}
                icon="eva:star-outline"
                color="warning"
              />

              <InfoField
                label="Price"
                value={data?.price ? `£${data.price.toFixed(2)}m` : "-"}
                icon="eva:credit-card-outline"
                color="success"
              />

              <InfoField
                label="Position"
                value={positionNames[data?.positionId] || data?.positionId || "-"}
                icon="eva:shield-outline"
                color="info"
              />

            </Grid>

            {/* Location Information */}
            <Typography variant="h6" className="view-team-section-title">
              Location Information
            </Typography>
            <Grid container spacing={3} className="view-team-section-grid">
              <InfoField
                label="Country"
                value={data?.countryDetails?.name || capitalize(leagueData?.country) || "-"}
                icon="eva:flag-outline"
                color="warning"
              />

              {/* <InfoField 
                label="City ID" 
                value={data?.cityId || "-"}
                icon="eva:map-pin-outline"
                color="info"
              /> */}
            </Grid>

            {/* League Teams Section */}
            <Divider className="view-team-divider" />
            <LeagueTeamsSection />

            <Box className="view-team-close-button-container">
              <Button
                variant="contained"
                onClick={onHide}
                className="view-team-close-button"
              >
                Close Profile
              </Button>
            </Box>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ViewTeamManagement;